import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, CircularProgress, IconButton, Tooltip, Typography, Chip, useMediaQuery,
} from '@mui/material';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon     from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import FullscreenIcon      from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon  from '@mui/icons-material/FullscreenExit';
import { getPitch } from './services/pitchService';
import { fontFamily } from '../../theme/tikabTokens';
import { useQRLogo } from '../../hooks/useQRLogo';
import { QRCodeSVG } from 'qrcode.react';
import ScaledIframe from './ScaledIframe';

const TYPE_STYLES = {
  html: { label: 'HTML', bg: '#00695C', color: '#fff' },
  pdf:  { label: 'PDF',  bg: '#B71C1C', color: '#fff' },
};

export default function ExternalPitchView() {
  const { pitchId } = useParams();
  const navigate    = useNavigate();
  const isMobile    = useMediaQuery('(max-width:599px)');
  const [pitch,      setPitch]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef();
  const qrLogo = useQRLogo();

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    getPitch(pitchId)
      .then(p => setPitch(p))
      .finally(() => setLoading(false));
  }, [pitchId]);

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pitch || !pitch.externalFileUrl) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Presentationen hittades inte.</Typography>
      </Box>
    );
  }

  const typeStyle = TYPE_STYLES[pitch.pitchType] || TYPE_STYLES.html;

  return (
    <Box ref={containerRef} sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#111' }}>

      {/* ── Header ── */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white',
        px: 2, py: 1,
        display: 'flex', alignItems: 'center', gap: 2,
        flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>

        <Chip
          label={typeStyle.label}
          size="small"
          sx={{
            bgcolor: typeStyle.bg, color: typeStyle.color,
            fontWeight: 700, fontSize: '0.68rem',
            fontFamily, flexShrink: 0,
          }}
        />

        <Typography fontWeight={700} noWrap sx={{ flex: 1, fontFamily }}>
          {pitch.title || 'Namnlös presentation'}
        </Typography>

        {pitch.client && (
          <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.45)', flexShrink: 0 }}>
            {pitch.client}
          </Typography>
        )}

        {/* Helskärm */}
        <Tooltip title={fullscreen ? 'Avsluta helskärm' : 'Helskärm'}>
          <IconButton color="inherit" size="small" onClick={toggleFullscreen}>
            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* Öppna i ny flik */}
        <Tooltip title="Öppna i ny flik">
          <IconButton
            color="inherit"
            size="small"
            onClick={() => window.open(pitch.externalFileUrl, '_blank')}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* AI-redigering — framtida funktion */}
        <Tooltip title={
          <Box>
            <Typography variant="caption" fontWeight={700}>AI-redigering (kommande)</Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
              Beskriv en ändring med ord — Claude läser HTML:en och returnerar
              en uppdaterad version. Planerad framtida funktion.
            </Typography>
          </Box>
        }>
          <span>
            <IconButton disabled size="small" sx={{ color: 'rgba(255,255,255,0.2) !important' }}>
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Iframe + QR-overlay ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {pitch.pitchType === 'html' ? (
          <ScaledIframe
            src={pitch.externalFileUrl}
            title={pitch.title}
            qrUrl={pitch.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
            qrLogo={qrLogo}
          />
        ) : (
          <>
            <iframe
              src={pitch.externalFileUrl}
              title={pitch.title || 'Presentation'}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
            {pitch.shareToken && !isMobile && (
              <Box sx={{
                position: 'absolute', bottom: 24, right: 24, zIndex: 10,
                bgcolor: 'rgba(0,0,0,0.52)', borderRadius: '8px', p: '6px',
                backdropFilter: 'blur(4px)', display: 'inline-flex',
                pointerEvents: 'none',
              }}>
                <QRCodeSVG
                  value={`${window.location.origin}/p/${pitch.shareToken}`}
                  size={88}
                  level="H"
                  bgColor="transparent"
                  fgColor="rgba(255,255,255,0.90)"
                  imageSettings={qrLogo ? { src: qrLogo, width: 22, height: 22, excavate: true } : undefined}
                />
              </Box>
            )}
          </>
        )}
      </Box>

    </Box>
  );
}
